import { supabase, getValidAccessToken, clearLocalAuthSession } from '../../lib/supabase';
import {
    readFunctionsInvokeErrorMessage,
    readFunctionsInvokeHttpStatus,
} from '../../lib/functionsInvoke';
import { classifyError, ClassifiedError } from '../../utils/networkError';
import { acceptChatSendResponse, recoverChatSendReceipt, ChatSendPendingError } from '../modules/chatSendReceipt';

let authBlockedUntil = 0;
/** Pausa chamadas UAZAPI por instância após 503 — evita martelar multitenant.
 * Envio (send/) usa cooldown curto para não travar o chat. */
const instanceCooldownUntil = new Map<string, number>();
const INSTANCE_COOLDOWN_MS = 15_000;
const SEND_COOLDOWN_MS = 2_000;

const MFA_MSG_RE = /\bmfa\b|aal2|verificação em 2 etapas|2 etapas/i;
const DEAD_SESSION_MSG_RE =
    /jwt|session_not_found|session not found|invalid claim|bad_jwt|refresh token|token ausente|token malformado|token inválido|não autenticado|não autorizado|expired|expirad/i;

function isMfaAuthMessage(message: string): boolean {
    return MFA_MSG_RE.test(message);
}

function isDeadSessionAuthMessage(message: string): boolean {
    if (!message || isMfaAuthMessage(message)) return false;
    return DEAD_SESSION_MSG_RE.test(message);
}

function isSendEndpoint(endpoint: string): boolean {
    return /\/?send\//i.test(endpoint);
}

function markInstanceCooldown(instanceName: string | undefined, endpoint?: string): void {
    if (!instanceName) return;
    const ms = endpoint && isSendEndpoint(endpoint) ? SEND_COOLDOWN_MS : INSTANCE_COOLDOWN_MS;
    instanceCooldownUntil.set(instanceName, Date.now() + ms);
}

function isInstanceOnCooldown(instanceName: string | undefined): boolean {
    if (!instanceName) return false;
    const until = instanceCooldownUntil.get(instanceName) || 0;
    return Date.now() < until;
}

function classifyUazapiError(error: unknown): ClassifiedError {
    const msg = error instanceof Error ? error.message : String(error);

    if (/token inválido|token ausente|não autorizado|não autenticado|401|jwt/i.test(msg) && !msg.includes('Uazapi API Error')) {
        return classifyError(error);
    }

    if (/Edge Function returned a non-2xx|FunctionsHttpError|FunctionsRelayError/i.test(msg)) {
        return {
            type: 'server',
            title: 'WhatsApp temporariamente indisponível',
            message: 'Não foi possível consultar o WhatsApp agora. Tente novamente em instantes.',
            isRetryable: true,
        };
    }

    if (msg.includes('Uazapi API Error')) {
        const statusMatch = msg.match(/Uazapi API Error (\d+)/);
        const status = statusMatch ? parseInt(statusMatch[1]) : 0;

        if (status === 401 || status === 403) {
            return {
                type: 'server',
                title: 'Conexão WhatsApp com problema',
                message: 'O token da instância WhatsApp está inválido ou expirou. Verifique a conexão nas configurações.',
                isRetryable: false,
            };
        }

        return {
            type: 'server',
            title: 'Erro na API do WhatsApp',
            message: `A API do WhatsApp retornou um erro (${status}). Tente novamente em alguns segundos.`,
            isRetryable: status >= 500,
        };
    }

    return classifyError(error);
}

function blockAuthTemporarily(): void {
    authBlockedUntil = Date.now() + 60000;
}

/** Sessão revogada (logout/login em outro dispositivo) — JWT local ainda válido mas getUser falha no servidor. */
async function invalidateStaleSession(): Promise<void> {
    blockAuthTemporarily();
    await clearLocalAuthSession();
}

const MFA_REQUIRED_MSG = 'Complete a verificação em 2 etapas para usar o WhatsApp.';
const SESSION_EXPIRED_MSG = 'Sua sessão expirou. Faça login novamente.';

/**
 * 401 do proxy: MFA ≠ matar sessão; tenta 1 refresh antes de limpar JWT morto.
 * Evita cascata logout 403 + contacts 42501 por access token só expirado.
 */
async function handleProxyUnauthorized(
    error: unknown,
    alreadyRetriedAuth: boolean,
): Promise<'retry'> {
    const bodyMsg = (await readFunctionsInvokeErrorMessage(error)) || '';
    const sdkMsg = error instanceof Error ? error.message : String(error ?? '');
    const combined = `${bodyMsg} ${sdkMsg}`;

    if (isMfaAuthMessage(combined)) {
        throw new Error(MFA_REQUIRED_MSG);
    }

    if (!alreadyRetriedAuth) {
        try {
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (!refreshError) return 'retry';
        } catch {
            /* cai no invalidate */
        }
    }

    if (isDeadSessionAuthMessage(combined) || !bodyMsg) {
        await invalidateStaleSession();
        throw new Error(SESSION_EXPIRED_MSG);
    }

    blockAuthTemporarily();
    throw new Error(bodyMsg || SESSION_EXPIRED_MSG);
}

export async function fetchAPI<T>(
    endpoint: string,
    options: RequestInit & { onAccepted?: () => void } = {},
    retries = 2,
    backoff = 2000
): Promise<T> {
    if (Date.now() < authBlockedUntil) {
        throw new Error('Usuário não autenticado. Faça login novamente.');
    }

    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const sendPath = isSendEndpoint(cleanEndpoint);
    const queuedPath = /^\/send\/(text|media|contact|location|pix-button)\/?$/i.test(cleanEndpoint);
    const requestId = sendPath ? (new Headers(options.headers).get('x-chat-request-id') || crypto.randomUUID()) : undefined;
    // Sem chave de idempotência do provedor, retry de um POST ambíguo pode enviar duas vezes.
    if (sendPath) retries = 1;
    const effectiveBackoff = sendPath ? Math.min(backoff, 500) : backoff;

    let instanceName: string | undefined;
    if (options.headers) {
        if (options.headers instanceof Headers) {
            instanceName = options.headers.get('instance') || options.headers.get('Instance') || undefined;
        } else if (!Array.isArray(options.headers)) {
            const h = options.headers as Record<string, string>;
            instanceName = h['instance'] || h['Instance'] || h['InstanceName'];
        }
    }

    if (isInstanceOnCooldown(instanceName)) {
        throw new Error('Aguarde alguns segundos antes de enviar outra mensagem.');
    }

    let requestData: unknown;
    if (options.body) {
        if (typeof options.body === 'string') {
            try { requestData = JSON.parse(options.body); }
            catch { requestData = options.body; }
        } else {
            requestData = options.body;
        }
    }

    let authRetried = false;

    for (let i = 0; i < retries; i++) {
        try {
            let token: string;
            try {
                token = await getValidAccessToken();
            } catch {
                blockAuthTemporarily();
                throw new Error('Usuário não autenticado. Faça login novamente.');
            }

            const { data, error } = await supabase.functions.invoke('uazapi-proxy', {
                body: {
                    endpoint: cleanEndpoint,
                    method: options.method || 'GET',
                    data: requestData,
                    instanceName,
                    ...(requestId ? { requestId } : {}),
                },
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });

            if (error) {
                const msg = error.message || '';
                const httpStatus = readFunctionsInvokeHttpStatus(error);
                if (queuedPath && requestId && (httpStatus == null || httpStatus >= 500)) {
                    return await recoverChatSendReceipt(requestId, options.onAccepted) as T;
                }
                if (httpStatus === 401) {
                    const action = await handleProxyUnauthorized(error, authRetried);
                    if (action === 'retry') {
                        authRetried = true;
                        i -= 1; // não consome retry de rede; só 1 refresh de auth
                        continue;
                    }
                }
                // CORS/network (ex.: OPTIONS 502 sem ACAO) — não martelar o console
                if (/failed to fetch|networkerror|load failed|cors/i.test(msg)) {
                    if (i < retries - 1) {
                        await new Promise(r => setTimeout(r, effectiveBackoff * (i + 1)));
                        continue;
                    }
                    throw new Error('WhatsApp temporariamente indisponível. Tente novamente em instantes.');
                }
                if (isMfaAuthMessage(msg)) {
                    throw new Error(MFA_REQUIRED_MSG);
                }
                if (/401|jwt|token|unauthorized|não autorizado|inválido|expired/i.test(msg)) {
                    blockAuthTemporarily();
                }
                const classified = classifyUazapiError(error);
                if (!classified.isRetryable) throw new Error(classified.message);
                if (i < retries - 1) {
                    await new Promise(r => setTimeout(r, effectiveBackoff * (i + 1)));
                    continue;
                }
                throw new Error(classified.message);
            }

            if (data && typeof data === 'object' && 'error' in data && (data as any).error) {
                const errMsg = String((data as any).error);
                const details = (data as any).details;
                const detailMsg =
                    typeof details === 'string'
                        ? details
                        : details?.message || details?.error || details?.msg || '';
                const fullMsg = detailMsg ? `${errMsg}: ${detailMsg}` : errMsg;
                if (isMfaAuthMessage(fullMsg)) {
                    throw new Error(MFA_REQUIRED_MSG);
                }
                if (/token inválido|não autorizado|não autenticado/i.test(fullMsg)) {
                    blockAuthTemporarily();
                }
                if (/Uazapi API Error 503/.test(errMsg)) {
                    markInstanceCooldown(instanceName, cleanEndpoint);
                    if (i < retries - 1) {
                        await new Promise(r => setTimeout(r, sendPath ? 800 : 4000));
                        continue;
                    }
                }
                throw new Error(fullMsg);
            }

            return (queuedPath && requestId ? await acceptChatSendResponse(data, requestId, options.onAccepted) : data) as T;

        } catch (error: any) {
            if (error instanceof ChatSendPendingError) throw error;
            if (
                error instanceof Error &&
                (error.message === MFA_REQUIRED_MSG || error.message === SESSION_EXPIRED_MSG)
            ) {
                throw error;
            }
            const classified = classifyUazapiError(error);
            const isLast = i === retries - 1;
            const isStatusProbe = /\/instance\/status/i.test(cleanEndpoint);
            const isAuthNoise = classified.type === 'auth';

            if (isLast || !classified.isRetryable) {
                if (classified.isRetryable) {
                    if (String(error.message || '').includes('503')) {
                        markInstanceCooldown(instanceName, cleanEndpoint);
                    }
                    // Probe de status falha com frequência (instância off / token) — não poluir console.error.
                    if (!isStatusProbe && !isAuthNoise) {
                        console.error(`[Uazapi Proxy] Falha final em ${cleanEndpoint}:`, classified.message);
                    }
                }
                throw new Error(classified.message);
            }

            await new Promise(r => setTimeout(r, effectiveBackoff * (i + 1)));
        }
    }

    throw new Error('Erro desconhecido no Uazapi Proxy');
}

export async function fetchArraySafe<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T[]> {
    try {
        const data = await fetchAPI<any>(endpoint, options);
        if (Array.isArray(data)) return data;
        if (data?.chats && Array.isArray(data.chats)) return data.chats;
        if (data?.messages && Array.isArray(data.messages)) return data.messages;
        if (data?.messages?.records && Array.isArray(data.messages.records)) return data.messages.records;
        return [];
    } catch (error) {
        console.error(`[Uazapi Proxy] Erro em ${endpoint}:`, error);
        return [];
    }
}

export async function fetchArraySilent<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T[]> {
    try {
        return await fetchArraySafe<T>(endpoint, options);
    } catch {
        return [];
    }
}
